import { registerSW } from 'virtual:pwa-register'

/**
 * Enregistrement du service worker et gestion de la persistance du stockage.
 *
 * L'app est en `registerType: 'prompt'` : quand une nouvelle version est
 * deployee, le service worker s'installe puis **reste en attente**. La page
 * continue de tourner sur la version installee, avec ses caches intacts, et
 * `onNeedRefresh` declenche le bandeau. Rien ne bascule tant que l'utilisateur
 * n'a pas accepte — une saisie en cours ne peut donc pas etre perdue.
 */

type Listener = (pret: boolean) => void

const listeners = new Set<Listener>()
let derniereValeur = false

export function onMiseAJour(l: Listener): () => void {
  listeners.add(l)
  if (derniereValeur) l(true)
  return () => listeners.delete(l)
}

function emettre(v: boolean) {
  derniereValeur = v
  for (const l of listeners) l(v)
}

/**
 * Fonction rendue par `registerSW`. L'appeler envoie `skipWaiting` au service
 * worker en attente, ce qui l'active et provoque le rechargement de la page.
 *
 * Sans elle, le bouton « Recharger » du bandeau serait sans effet : un simple
 * `location.reload()` ne suffit pas a activer un service worker en attente, la
 * page reviendrait a l'identique sur l'ancienne version.
 */
let appliquer: ((rechargerPage?: boolean) => Promise<void>) | null = null

export function enregistrerServiceWorker(): void {
  if (import.meta.env.DEV) return

  appliquer = registerSW({
    immediate: true,
    onNeedRefresh() {
      emettre(true)
    },
    onOfflineReady() {
      // Rien a signaler : l'app est concue pour etre hors ligne par defaut.
    },
    onRegisterError(e) {
      console.error('Echec de l\'enregistrement du service worker', e)
    },
  })
}

/** Bascule sur la nouvelle version. La page se recharge d'elle-meme ensuite. */
export async function appliquerMiseAJour(): Promise<void> {
  if (!appliquer) {
    window.location.reload()
    return
  }
  await appliquer(true)
  // Filet de securite : le rechargement est normalement declenche par
  // l'evenement `controlling` du nouveau service worker. Safari sur iOS est
  // capricieux sur ce point ; si rien ne s'est passe apres deux secondes, on
  // force. Si le rechargement a bien eu lieu, ce minuteur n'existe deja plus.
  setTimeout(() => window.location.reload(), 2000)
}

/**
 * Demande au navigateur de ne pas evincer les donnees sous pression de stockage.
 *
 * Une PWA installee sur iOS echappe deja a la purge Safari des 7 jours
 * d'inactivite, mais l'appel reste une securite peu couteuse — et il est
 * necessaire sur les autres plateformes. Le navigateur peut refuser sans que
 * ce soit une erreur.
 */
export async function demanderPersistance(): Promise<boolean> {
  if (!navigator.storage?.persist) return false
  try {
    if (await navigator.storage.persisted()) return true
    return await navigator.storage.persist()
  } catch {
    return false
  }
}

export interface QuotaStockage {
  utiliseOctets: number
  quotaOctets: number
  persistant: boolean
}

export async function lireQuota(): Promise<QuotaStockage | null> {
  if (!navigator.storage?.estimate) return null
  try {
    const est = await navigator.storage.estimate()
    const persistant = navigator.storage.persisted ? await navigator.storage.persisted() : false
    return {
      utiliseOctets: est.usage ?? 0,
      quotaOctets: est.quota ?? 0,
      persistant,
    }
  } catch {
    return null
  }
}

/** `true` si l'app tourne en mode installe (ecran d'accueil) plutot que dans Safari. */
export function estInstallee(): boolean {
  const nav = navigator as Navigator & { standalone?: boolean }
  return (
    window.matchMedia('(display-mode: standalone)').matches || nav.standalone === true
  )
}

export function estIOS(): boolean {
  const ua = navigator.userAgent
  // iPadOS 13+ se presente comme un Mac : on le distingue par le support tactile.
  return /iPad|iPhone|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1)
}
